/**
 * Still frames for animated avatars.
 *
 * An animated picture that loops forever in every list is exhausting - Discord
 * shows a frozen frame and only plays it when you hover, or when you are
 * actually looking at the profile. Same here.
 *
 * The still is the first frame drawn to a canvas once, then cached, so a GIF
 * that appears twenty times in a chat log costs one decode.
 */

const stills = new Map<string, string>();
const pending = new Map<string, Promise<string>>();

/** True for anything that moves. Only GIF for now; APNG/WebP would need a parse. */
export function isAnimated(url: string | undefined | null): boolean {
  return /\.gif(\?|$)/i.test(url ?? '');
}

/** The cached still, if one has already been made. Lets a render go straight to it. */
export function cachedStill(url: string): string | null {
  return stills.get(url) ?? null;
}

/**
 * First frame of `url` as a data URL.
 *
 * Falls back to the original URL if the image cannot be read - a cross-origin
 * image without CORS headers taints the canvas, and a moving avatar beats a
 * broken one.
 */
export function stillFrame(url: string): Promise<string> {
  const done = stills.get(url);
  if (done) return Promise.resolve(done);

  const inflight = pending.get(url);
  if (inflight) return inflight;

  // Fetched as a blob rather than assigned to an Image src: a copy already in
  // the cache from a non-CORS request would taint the canvas and the still
  // would silently fall back to the moving original.
  const work = (async () => {
    try {
      const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
      if (!res.ok) throw new Error('http ' + res.status);
      const bitmap = await createImageBitmap(await res.blob());

      const size = Math.min(160, Math.max(bitmap.width, 1));
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = Math.max(1, Math.round(size * (bitmap.height / bitmap.width)));
      canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      bitmap.close();

      const data = canvas.toDataURL('image/png');
      stills.set(url, data);
      return data;
    } catch {
      stills.set(url, url);
      return url;
    } finally {
      pending.delete(url);
    }
  })();

  pending.set(url, work);
  return work;
}
